// src/pages/Login.tsx
import React, { useState } from 'react';
import { api } from '../lib/api';
import { Link, useNavigate } from 'react-router-dom';

interface Props { onLogin: (jwt: string, username?: string) => void; }

const Login: React.FC<Props> = ({ onLogin }) => {
  const nav = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setMsg(null); setResendMsg(null); setPendingEmail(null);
    setLoading(true);
    try {
      const res = await api.post('/user/login', { username, password });
      if (res.data?.token) {
        const token = res.data.token;
        // decode username (best effort) for header
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          onLogin(token, payload?.username);
        } catch { onLogin(token, username); }
        setMsg('Login successful');
        nav('/kb-upload');
      } else {
        setErr(res.data?.message || 'Login failed');
      }
    } catch (e: any) {
      const code = e?.response?.status;
      const text = e?.response?.data?.error || e?.message || 'Login failed';
      setErr(text);
      if (code === 403 && text.toLowerCase().includes('email')) {
        setPendingEmail('');
      }
    } finally { setLoading(false); }
  };

  const resendVerification = async () => {
    if (!pendingEmail) return;
    setResending(true);
    try {
      const r = await api.post('/user/request-verification', { email: pendingEmail });
      setResendMsg(r.data?.message || 'Verification email sent.');
    } catch (e: any) {
      setResendMsg(e?.response?.data?.error || e?.message || 'Could not send verification email.');
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white w-full max-w-md rounded-xl shadow p-8">
        <h1 className="text-2xl font-semibold mb-6 text-center">Login</h1>
        <label className="block text-sm mb-1">Username</label>
        <input className="w-full mb-4 px-3 py-2 border rounded" value={username} onChange={e=>setUsername(e.target.value)} />
        <label className="block text-sm mb-1">Password</label>
        <input type="password" className="w-full mb-6 px-3 py-2 border rounded" value={password} onChange={e=>setPassword(e.target.value)} />
        <button disabled={loading} className="w-full bg-blue-600 text-white py-2 rounded">
          {loading ? 'Logging in…' : 'Login'}
        </button>

        {msg && <div className="mt-4 text-green-600 text-sm">{msg}</div>}
        {err && <div className="mt-4 text-red-600 text-sm">{err}</div>}

        {pendingEmail !== null && (
          <div className="mt-6 border rounded p-4 bg-yellow-50">
            <div className="text-sm mb-2">Your email isn’t verified. Enter your email to resend the verification link:</div>
            <input type="email" className="w-full mb-2 px-3 py-2 border rounded"
              value={pendingEmail} onChange={e=>setPendingEmail(e.target.value)} placeholder="you@example.com" />
            <button type="button" onClick={resendVerification}
              className="w-full bg-amber-600 text-white py-2 rounded" disabled={resending || !pendingEmail}>
              {resending ? 'Sending…' : 'Resend verification email'}
            </button>
            {resendMsg && <div className="mt-2 text-sm">{resendMsg}</div>}
          </div>
        )}

        <div className="mt-6 text-center text-sm">
          Don’t have an account? <Link to="/register" className="text-blue-600">Sign up</Link>
        </div>
      </form>
    </div>
  );
};

export default Login;
